<script setup>
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormLegend from '@js/components/forms/FormLegend.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import FormTitle from '@js/components/forms/FormTitle.vue';
import TabLayout from '@js/components/tabs/TabLayout.vue';
import { goBack, useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { computed, onMounted, reactive, ref } from 'vue';
import { string, object } from 'yup';

const { tenant, semSegmentation, semSegmentationTypes, productAttributes } =
    defineProps({
        tenant: {
            required: true,
            type: Object,
        },
        semSegmentation: {
            required: false,
            type: Object,
        },
        semSegmentationTypes: {
            required: true,
            type: Array,
        },
        productAttributes: {
            required: true,
            type: Array,
        },
    });

const { route } = useRoute();

/** @type {{ type: { label: string, value: string }, data: Record<string, any>, cmsCustomerGroups: Array<Object>|null, name: string }} */
const form = reactive({
    type: semSegmentation
        ? semSegmentationTypes.find(
              (type) => type.value === semSegmentation.type.value,
          )
        : semSegmentationTypes[0],
    data: semSegmentation ? semSegmentation.data : {},
    cmsCustomerGroups: null,
    filters: initializeFilters(),
    name: semSegmentation ? semSegmentation.name : '',
});

const isSubmitting = ref(false);

const loading = ref(false);

const eCommerceCustomerGroups = ref([]);

const validationSchema = computed(() => {
    let schema = {
        type: object().required('Le type est requis'),
        name: string().required('Le nom est requis'),
    };

    if (
        form.type.value === 'customers_with_no_purchase_last_n_days' ||
        form.type.value === 'customers_with_n_or_more_orders' ||
        form.type.value === 'customers_with_unprocessed_basket_last_n_days'
    ) {
        schema = {
            ...schema,
            data: object().shape({
                n: string().required('Une valeur pour N est requise'),
            }),
        };
    }

    if (form.type.value === 'customers_with_n_or_more_orders_last_t_days') {
        schema = {
            ...schema,
            data: object().shape({
                n: string().required('Une valeur pour N est requise'),
                t: string().required('Une valeur pour T est requises'),
            }),
        };
    }

    return object(schema);
});

function updateData() {
    loading.value = true;

    Promise.all([
        axios.get(
            route('front.eCommerce.getCustomerGroups', { tenant: tenant.id }),
        ),
    ])
        .then(([eCommerceCustomerGroupResponse]) => {
            eCommerceCustomerGroups.value = eCommerceCustomerGroupResponse.data;

            form.cmsCustomerGroups = eCommerceCustomerGroups.value.filter(
                (group) =>
                    semSegmentation &&
                    semSegmentation.cmsCustomerGroupIds.includes(
                        group.cmsCustomerGroupId,
                    ),
            );
        })
        .catch((error) => {
            console.error(error);
        })
        .finally(() => {
            loading.value = false;
        });
}

function initializeFilters() {
    const rawFilters =
        semSegmentation &&
        semSegmentation.filters &&
        !Array.isArray(semSegmentation.filters)
            ? semSegmentation.filters
            : {};

    const rawProductAttributes = rawFilters.productAttributes || {};

    const productAttributeFilters = {};

    for (const productAttribute of productAttributes) {
        if (productAttribute.valueType !== 'option') {
            continue;
        }

        const values = rawProductAttributes[productAttribute.code];

        productAttributeFilters[productAttribute.code] = (
            Array.isArray(values) ? values : []
        )
            .map((value) =>
                (productAttribute.options || []).find(
                    (option) => option.value === value,
                ),
            )
            .filter((option) => option !== undefined);
    }

    return {
        productAttributes: productAttributeFilters,
        score: {
            min: rawFilters.score?.min ?? null,
            max: rawFilters.score?.max ?? null,
        },
    };
}

function buildFilters() {
    const result = {};

    const productAttributeFilters = {};

    for (const [code, options] of Object.entries(
        form.filters.productAttributes,
    )) {
        if (Array.isArray(options) && options.length > 0) {
            productAttributeFilters[code] = options.map(
                (option) => option.value,
            );
        }
    }

    if (Object.keys(productAttributeFilters).length > 0) {
        result.productAttributes = productAttributeFilters;
    }

    const score = {};

    if (form.filters.score.min !== null && form.filters.score.min !== '') {
        score.min = Number(form.filters.score.min);
    }

    if (form.filters.score.max !== null && form.filters.score.max !== '') {
        score.max = Number(form.filters.score.max);
    }

    if (Object.keys(score).length > 0) {
        result.score = score;
    }

    return Object.keys(result).length > 0 ? result : null;
}

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.sem.postSegmentation', {
                tenant: tenant.id,
                ...(semSegmentation
                    ? { semSegmentation: semSegmentation.id }
                    : {}),
            }),
            {
                type: form.type.value,
                data: form.data,
                cmsCustomerGroupIds:
                    form.cmsCustomerGroups && form.cmsCustomerGroups.length > 0
                        ? form.cmsCustomerGroups.map(
                              (group) => group.cmsCustomerGroupId,
                          )
                        : null,
                filters: buildFilters(),
                name: form.name,
            },
        )
        .then(() => {
            toastSaved();

            goBack();
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);

            router.visit(
                route('front.projectManagement.sem.segmentations', {
                    tenant: tenant.id,
                }),
            );
        });
}

onMounted(() => {
    updateData();
});
</script>

<template>
    <FormRoot
        v-slot="{ errors }"
        :validation-schema="validationSchema"
        :submit="submit"
    >
        <TabLayout
            :tabs="{
                information: {
                    title: 'Informations',
                    contentSlotName: 'information',
                    forceMount: true,
                },
                filters: {
                    title: 'Filtres',
                    contentSlotName: 'filters',
                    forceMount: true,
                },
            }"
            :loading="loading"
        >
            <template #information>
                <FormColumn>
                    <FormContent>
                        <InputField
                            v-model="form.name"
                            label="Nom"
                            field-name="name"
                            field-id="name"
                            type="text"
                            required
                        />

                        <FormGrid :columns="2">
                            <ListboxField
                                v-model="form.type"
                                field-id="type"
                                field-name="type"
                                label="Type"
                                :options="semSegmentationTypes"
                                required
                            >
                                <template #buttonContent>
                                    {{ form.type.label }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.label }}
                                </template>
                            </ListboxField>
                        </FormGrid>
                    </FormContent>

                    <FormContent
                        v-if="
                            form.type.value ===
                                'customers_with_no_purchase_last_n_days' ||
                            form.type.value ===
                                'customers_with_n_or_more_orders' ||
                            form.type.value ===
                                'customers_with_n_or_more_orders_last_t_days' ||
                            form.type.value ===
                                'customers_with_unprocessed_basket_last_n_days'
                        "
                    >
                        <FormTitle> Paramètres </FormTitle>

                        <FormGrid :columns="4">
                            <InputField
                                v-if="
                                    form.type.value ===
                                        'customers_with_no_purchase_last_n_days' ||
                                    form.type.value ===
                                        'customers_with_n_or_more_orders' ||
                                    form.type.value ===
                                        'customers_with_n_or_more_orders_last_t_days' ||
                                    form.type.value ===
                                        'customers_with_unprocessed_basket_last_n_days'
                                "
                                v-model="form.data.n"
                                label="N"
                                field-name="data.n"
                                field-id="data.n"
                                type="number"
                                step="1"
                                min="1"
                                required
                            />

                            <InputField
                                v-if="
                                    form.type.value ===
                                    'customers_with_n_or_more_orders_last_t_days'
                                "
                                v-model="form.data.t"
                                label="T"
                                field-name="data.t"
                                field-id="data.t"
                                type="number"
                                step="1"
                                min="1"
                                required
                            />
                        </FormGrid>
                    </FormContent>
                </FormColumn>
            </template>

            <template #filters>
                <FormColumn>
                    <FormContent>
                        <FormTitle>Groupes clients</FormTitle>

                        <FormGrid :columns="2">
                            <ListboxField
                                v-model="form.cmsCustomerGroups"
                                field-id="cmsCustomerGroups"
                                field-name="cmsCustomerGroups"
                                label="Groupes clients"
                                :options="eCommerceCustomerGroups"
                                multiple
                                has-empty-option
                            >
                                <template #buttonContent>
                                    <template
                                        v-if="
                                            form.cmsCustomerGroups &&
                                            form.cmsCustomerGroups.length > 0
                                        "
                                    >
                                        {{
                                            form.cmsCustomerGroups
                                                .map((group) => group.code)
                                                .join(', ')
                                        }}
                                    </template>

                                    <template v-else> &nbsp; </template>
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.code }}
                                </template>
                            </ListboxField>
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormTitle>Attributs produits</FormTitle>

                        <FormGrid
                            v-if="
                                productAttributes &&
                                productAttributes.length > 0
                            "
                            :columns="2"
                        >
                            <template
                                v-for="(
                                    productAttribute, index
                                ) in productAttributes"
                                :key="index"
                            >
                                <ListboxField
                                    v-if="
                                        productAttribute.valueType ===
                                            'option' &&
                                        productAttribute.options &&
                                        productAttribute.options.length > 0
                                    "
                                    v-model="
                                        form.filters.productAttributes[
                                            productAttribute.code
                                        ]
                                    "
                                    :field-id="`filters${productAttribute.code}`"
                                    :field-name="`filters${productAttribute.code}`"
                                    :label="productAttribute.label"
                                    :options="productAttribute.options"
                                    has-empty-option
                                    multiple
                                    by="value"
                                >
                                    <template #buttonContent>
                                        <template
                                            v-if="
                                                form.filters.productAttributes[
                                                    productAttribute.code
                                                ] &&
                                                form.filters.productAttributes[
                                                    productAttribute.code
                                                ].length > 0
                                            "
                                        >
                                            {{
                                                form.filters.productAttributes[
                                                    productAttribute.code
                                                ]
                                                    .map((item) => item.label)
                                                    .join(', ')
                                            }}
                                        </template>

                                        <template v-else> &nbsp; </template>
                                    </template>

                                    <template #optionContent="{ option }">
                                        {{ option.label }}
                                    </template>
                                </ListboxField>
                            </template>
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormTitle>Score client</FormTitle>

                        <FormGrid :columns="2">
                            <InputField
                                v-model="form.filters.score.min"
                                label="Score minimum (%)"
                                field-name="filters.score.min"
                                field-id="filters.score.min"
                                type="number"
                                step="1"
                                min="0"
                                max="100"
                            />

                            <InputField
                                v-model="form.filters.score.max"
                                label="Score maximum (%)"
                                field-name="filters.score.max"
                                field-id="filters.score.max"
                                type="number"
                                step="1"
                                min="0"
                                max="100"
                            />
                        </FormGrid>
                    </FormContent>
                </FormColumn>
            </template>
        </TabLayout>

        <FormFooter :is-submitting="isSubmitting" :errors="errors">
            <template #actions>
                <DeleteAction
                    v-if="semSegmentation"
                    :url="
                        route(
                            'front.projectManagement.sem.deleteSegmentation',
                            {
                                tenant: tenant.id,
                                semSegmentation: semSegmentation.id,
                            },
                        )
                    "
                    :callback="
                        () => {
                            router.visit(
                                route(
                                    'front.projectManagement.sem.segmentations',
                                    {
                                        tenant: tenant.id,
                                    },
                                ),
                            );
                        }
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
